import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import FormularioConsultaVisitante from './FormularioConsultaVisitante';
import BannerFlotante from './BannerFlotante';
import 'react-quill/dist/quill.snow.css';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const navigate = useNavigate();
  const [bienvenida, setBienvenida] = useState(null);
  const [seccionAdicional, setSeccionAdicional] = useState(null);
  const [seccionesDinamicas, setSeccionesDinamicas] = useState([]);
  const [profesores, setProfesores] = useState([]);
  const [eventosPublicos, setEventosPublicos] = useState([]);
  const [noticiasPublicas, setNoticiasPublicas] = useState([]);
  const [pedidosPublicos, setPedidosPublicos] = useState([]);
  const [eventoEnVivo, setEventoEnVivo] = useState(null);
  const [eventoEnVivoStatus, setEventoEnVivoStatus] = useState('sin_eventos');
  // Transmisiones públicas del Comité Evangelístico (evangelismo / campañas / etc.)
  const [comiteTransmisiones, setComiteTransmisiones] = useState([]);
  const [comiteYoutubeEmbed, setComiteYoutubeEmbed] = useState(null);
  const [comiteYoutubeChannel, setComiteYoutubeChannel] = useState(null);
  const [comiteSeccionActiva, setComiteSeccionActiva] = useState(false);
  const [comiteProxima, setComiteProxima] = useState(null);
  const [redesSociales, setRedesSociales] = useState({
    facebook_url: '',
    instagram_url: '',
    youtube_url: '',
    twitter_url: '',
    linkedin_url: '',
    tiktok_url: '',
    webpage_url_1: '',
    webpage_url_1_nombre: '',
    webpage_url_2: '',
    webpage_url_2_nombre: '',
    webpage_url_3: '',
    webpage_url_3_nombre: ''
  });
  
  // Estado para pedido de oración
  const [pedidoOracion, setPedidoOracion] = useState({
    nombre: '',
    email: '',
    pedido: '',
    publico: false
  });
  const [enviandoPedido, setEnviandoPedido] = useState(false);
  const [pedidoEnviado, setPedidoEnviado] = useState(false);
  
  // Estado para clases del semestre activo
  const [clasesActivas, setClasesActivas] = useState([]);
  const [claseActualIndex, setClaseActualIndex] = useState(0);
  const [bannerSemestreConfig, setBannerSemestreConfig] = useState({ activo: true, color_fondo: '#3b82f6' });
  
  // Estado para clases extracurriculares y ministeriales
  const [clasesExtracurriculares, setClasesExtracurriculares] = useState([]);
  const [claseExtraIndex, setClaseExtraIndex] = useState(0);
  const [bannerExtraConfig, setBannerExtraConfig] = useState({ activo: false, color_fondo: '#9333ea' });

  useEffect(() => {
    cargarDatos();
  }, []);

  // Polling para transmisiones públicas del Comité (cada 30s)
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/public/comite/transmisiones-en-vivo`);
        setComiteTransmisiones(r.data.transmisiones || []);
        setComiteYoutubeEmbed(r.data.youtube_embed_url || null);
        setComiteSeccionActiva(!!r.data.seccion_activa);
        setComiteProxima(r.data.proxima || null);
      } catch (_e) { /* silencioso */ }
    }, 30000);
    return () => clearInterval(t);
  }, []);

  // Auto-slide para el carrusel de clases
  useEffect(() => {
    if (clasesActivas.length > 1) {
      const interval = setInterval(() => {
        setClaseActualIndex((prev) => (prev + 1) % clasesActivas.length);
      }, 4000); // Cambiar cada 4 segundos
      return () => clearInterval(interval);
    }
  }, [clasesActivas.length]);
  
  // Auto-slide para el carrusel de clases extracurriculares
  useEffect(() => {
    if (clasesExtracurriculares.length > 1) {
      const interval = setInterval(() => {
        setClaseExtraIndex((prev) => (prev + 1) % clasesExtracurriculares.length);
      }, 4500); // Cambiar cada 4.5 segundos (ligeramente diferente al otro)
      return () => clearInterval(interval);
    }
  }, [clasesExtracurriculares.length]);

  const cargarDatos = async () => {
    try {
      // Cargar bienvenida
      const bienvenidaRes = await axios.get(`${API}/public/bienvenida`);
      setBienvenida(bienvenidaRes.data);
      
      // Cargar sección adicional
      const seccionRes = await axios.get(`${API}/public/seccion-adicional`);
      setSeccionAdicional(seccionRes.data);
      
      // Cargar secciones dinámicas
      const seccionesRes = await axios.get(`${API}/public/secciones-landing`);
      setSeccionesDinamicas(seccionesRes.data);
      
      // Cargar profesores
      const profesoresRes = await axios.get(`${API}/public/profesores`);
      setProfesores(profesoresRes.data);
      
      // Cargar clases del semestre activo y su configuración
      try {
        const [clasesRes, bannerSemConfigRes] = await Promise.all([
          axios.get(`${API}/public/clases-semestre-activo`),
          axios.get(`${API}/public/banner-semestre-config`)
        ]);
        setClasesActivas(clasesRes.data);
        setBannerSemestreConfig(bannerSemConfigRes.data);
      } catch (e) {
        console.log('No hay clases activas');
      }
      
      // Cargar clases extracurriculares y ministeriales
      try {
        const [clasesExtraRes, bannerConfigRes] = await Promise.all([
          axios.get(`${API}/public/clases-extracurriculares-ministeriales`),
          axios.get(`${API}/public/banner-extracurriculares-config`)
        ]);
        setClasesExtracurriculares(clasesExtraRes.data);
        setBannerExtraConfig(bannerConfigRes.data);
      } catch (e) {
        console.log('No hay clases extracurriculares');
      }
      
      // Cargar eventos públicos
      const eventosRes = await axios.get(`${API}/public/eventos`);
      setEventosPublicos(eventosRes.data);
      
      // Cargar noticias públicas
      const noticiasRes = await axios.get(`${API}/public/noticias`);
      setNoticiasPublicas(noticiasRes.data);
      
      // Cargar pedidos de oración públicos
      const pedidosRes = await axios.get(`${API}/public/oracion/publicos`);
      setPedidosPublicos(pedidosRes.data);
      
      // Cargar redes sociales
      const redesRes = await axios.get(`${API}/public/redes-sociales`);
      setRedesSociales(redesRes.data);
      
      // Cargar evento en vivo (para sección YouTube)
      try {
        const eventoEnVivoRes = await axios.get(`${API}/public/evento-en-vivo`);
        setEventoEnVivoStatus(eventoEnVivoRes.data.status);
        setEventoEnVivo(eventoEnVivoRes.data.evento);
      } catch (e) {
        console.log('No hay evento en vivo');
      }

      try {
        const comiteRes = await axios.get(`${API}/public/comite/transmisiones-en-vivo`);
        setComiteTransmisiones(comiteRes.data.transmisiones || []);
        setComiteYoutubeEmbed(comiteRes.data.youtube_embed_url || null);
        setComiteYoutubeChannel(comiteRes.data.youtube_channel_url || null);
        setComiteSeccionActiva(!!comiteRes.data.seccion_activa);
        setComiteProxima(comiteRes.data.proxima || null);
      } catch (e) {
        // silencioso
      }
      
    } catch (error) {
      console.error('Error cargando datos públicos:', error);
    }
  };

  const handleEnviarPedido = async (e) => {
    e.preventDefault();
    setEnviandoPedido(true);

    try {
      await axios.post(`${API}/public/oracion`, pedidoOracion);
      setPedidoEnviado(true);
      setPedidoOracion({ nombre: '', email: '', pedido: '', publico: false });
      
      // Recargar pedidos públicos
      setTimeout(() => {
        cargarDatos();
        setPedidoEnviado(false);
      }, 3000);
      
    } catch (error) {
      alert('Error al enviar pedido de oración');
    } finally {
      setEnviandoPedido(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      // Convertir UTC a hora de Montevideo (mismo método que Clases.js)
      const utcDate = new Date(dateString);
      const montevideoDate = new Date(utcDate.getTime() - (3 * 60 * 60 * 1000));
      return montevideoDate.toLocaleDateString('es-UY', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric'
      });
    } catch {
      return 'Fecha inválida';
    }
  };
  
  const formatTime = (dateString) => {
    try {
      // Convertir UTC a hora de Montevideo (mismo método que Clases.js)
      const utcDate = new Date(dateString);
      const montevideoDate = new Date(utcDate.getTime() - (3 * 60 * 60 * 1000));
      return montevideoDate.toLocaleTimeString('es-UY', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return 'Hora inválida';
    }
  };
  
  const isEventToday = (dateString) => {
    const eventDate = new Date(dateString + 'Z');
    const montevideoDate = new Date(eventDate.getTime() - (3 * 60 * 60 * 1000));
    const today = new Date();
    
    return montevideoDate.getDate() === today.getDate() &&
           montevideoDate.getMonth() === today.getMonth() &&
           montevideoDate.getFullYear() === today.getFullYear();
  };

  const handleAccessSystem = () => {
    // Ir directo al login sin mostrar modal
    navigate('/app');
  };

  // Función para extraer el ID de YouTube de una URL
  const getYouTubeId = (url) => {
    if (!url) return null;
    
    // Soportar múltiples formatos:
    // https://www.youtube.com/watch?v=VIDEO_ID
    // https://youtu.be/VIDEO_ID
    // https://youtube.com/live/VIDEO_ID
    // https://www.youtube.com/embed/VIDEO_ID
    
    // Formato youtube.com/live/VIDEO_ID
    const liveMatch = url.match(/youtube\.com\/live\/([a-zA-Z0-9_-]{11})/);
    if (liveMatch) return liveMatch[1];
    
    // Formato youtu.be/VIDEO_ID
    const shortMatch = url.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
    if (shortMatch) return shortMatch[1];
    
    // Formato youtube.com/watch?v=VIDEO_ID
    const watchMatch = url.match(/[?&]v=([a-zA-Z0-9_-]{11})/);
    if (watchMatch) return watchMatch[1];
    
    // Formato youtube.com/embed/VIDEO_ID
    const embedMatch = url.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/);
    if (embedMatch) return embedMatch[1];
    
    return null;
  };

  // Componente de sección de YouTube en vivo
  const SeccionYouTubeEnVivo = () => {
    if (!eventoEnVivo) return null;
    
    const youtubeId = getYouTubeId(eventoEnVivo.youtube_url);
    if (!youtubeId) return null;
    
    return (
      <section className="mb-16">
        <Card className="shadow-2xl border-0 overflow-hidden bg-gradient-to-br from-gray-900 via-red-900 to-gray-900">
          <div className="p-6 text-white">
            {/* Header */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-3">
                <div className={`w-4 h-4 rounded-full ${eventoEnVivoStatus === 'en_vivo' ? 'bg-red-500 animate-pulse' : 'bg-gray-400'}`}></div>
                <h2 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
                  <i className="fab fa-youtube text-red-500"></i>
                  {eventoEnVivoStatus === 'en_vivo' ? '🔴 EN VIVO' : eventoEnVivoStatus === 'programado' ? '📅 PRÓXIMO EVENTO' : '📹 ÚLTIMO EVENTO'}
                </h2>
              </div>
              {eventoEnVivoStatus === 'en_vivo' && (
                <Badge className="bg-red-600 text-white animate-pulse px-4 py-2 text-lg">
                  <i className="fas fa-broadcast-tower mr-2"></i>
                  Transmitiendo ahora
                </Badge>
              )}
            </div>
            
            {/* Título del evento */}
            <h3 className="text-xl md:text-2xl font-semibold mb-2 text-center md:text-left">
              {eventoEnVivo.titulo}
            </h3>
            
            {/* Fecha y hora */}
            <p className="text-gray-300 mb-6 text-center md:text-left">
              <i className="far fa-calendar-alt mr-2"></i>
              {formatDate(eventoEnVivo.fecha_inicio)} - {formatTime(eventoEnVivo.fecha_inicio)}
            </p>
            
            {/* Video de YouTube embebido */}
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                className="absolute top-0 left-0 w-full h-full rounded-xl shadow-2xl"
                src={`https://www.youtube.com/embed/${youtubeId}?autoplay=${eventoEnVivoStatus === 'en_vivo' ? 1 : 0}&rel=0`}
                title={eventoEnVivo.titulo}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            
            {/* Descripción */}
            {eventoEnVivo.descripcion && (
              <p className="mt-6 text-gray-300 text-center md:text-left">
                {eventoEnVivo.descripcion}
              </p>
            )}
            
            {/* Botón para ver en YouTube */}
            <div className="mt-6 flex justify-center md:justify-start">
              <Button
                onClick={() => window.open(eventoEnVivo.youtube_url, '_blank')}
                className="bg-red-600 hover:bg-red-700 text-white px-6 py-3"
              >
                <i className="fab fa-youtube mr-2"></i>
                Ver en YouTube
              </Button>
            </div>
          </div>
        </Card>
      </section>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* BANNER FLOTANTE */}
      <BannerFlotante />
      
      {/* HEADER / BANNER */}
      <header className="bg-gradient-to-r from-blue-900 via-purple-900 to-blue-900 text-white shadow-2xl">
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            {/* Logo y Nombre */}
            <div className="flex flex-col md:flex-row items-center md:items-center gap-4">
              <div className="w-20 h-20 bg-white flex items-center justify-center shadow-lg p-2">
                <img 
                  src="/logo_instituto_512.png" 
                  alt="Logo IBA" 
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="text-center md:text-left">
                <h1 className="text-3xl md:text-4xl font-bold">Instituto Bíblico Apostólico</h1>
                <p className="text-blue-200 text-lg">Uruguay</p>
                <p className="text-blue-300 text-base italic mt-1">
                  Iglesia Pentecostal Unida del Uruguay
                </p>
                <p className="text-blue-300 text-sm flex items-center justify-center md:justify-start gap-2 mt-1">
                  <i className="fas fa-map-marker-alt"></i>
                  8 de Octubre 2816, Montevideo, Uruguay CP12400
                </p>
              </div>
            </div>
            
            {/* Navegación */}
            <div className="flex flex-col md:flex-row items-center gap-4">
              <nav className="flex flex-wrap justify-center gap-3 md:gap-6 text-sm md:text-lg">
                <a href="#inicio" className="hover:text-blue-300 transition">Inicio</a>
                <a href="#eventos" className="hover:text-blue-300 transition">Eventos</a>
                <a href="#noticias" className="hover:text-blue-300 transition">Noticias</a>
                <a href="#oracion" className="hover:text-blue-300 transition">Oración</a>
                <a href="#consultas" className="hover:text-blue-300 transition">Consultas</a>
              </nav>
            </div>
          </div>
        </div>
      </header>

      {/* BOTÓN FLOTANTE ACCESO AL SISTEMA */}
      <div className="fixed top-4 right-4 z-50">
        <Button
          onClick={handleAccessSystem}
          className="bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600 text-white font-bold px-4 py-2 md:px-6 md:py-3 shadow-2xl text-sm md:text-lg rounded-full animate-pulse hover:animate-none transition-all duration-300 hover:scale-105"
          data-testid="access-system-floating-btn"
        >
          <i className="fas fa-sign-in-alt mr-2"></i>
          <span className="hidden sm:inline">Acceso al Sistema</span>
          <span className="sm:hidden">Acceder</span>
        </Button>
      </div>

      {/* CONTENIDO PRINCIPAL */}
      <main className="container mx-auto px-4 py-12">
        {/* SECCIÓN DE BIENVENIDA Y EVENTOS (lado a lado) */}
        <section id="inicio" className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {/* BIENVENIDA - Columna Izquierda */}
          <div>
            <Card className="shadow-2xl border-0 overflow-hidden h-full">
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
                <h2 className="text-3xl font-bold flex items-center gap-3">
                  <i className="fas fa-book-open"></i>
                  {bienvenida?.titulo || 'Bienvenidos'}
                </h2>
              </div>
              <CardContent className="p-6">
                <style>{`
                  .ql-editor {
                    padding: 0 !important;
                  }
                  .bienvenida-content p {
                    margin-bottom: 1em;
                    line-height: 1.6;
                  }
                  .bienvenida-content strong {
                    font-weight: 700;
                    color: #1f2937;
                  }
                  .bienvenida-content em {
                    font-style: italic;
                  }
                  .bienvenida-content u {
                    text-decoration: underline;
                  }
                  .bienvenida-content h1 {
                    font-size: 2em;
                    font-weight: bold;
                    margin-bottom: 0.5em;
                    margin-top: 0.5em;
                  }
                  .bienvenida-content h2 {
                    font-size: 1.5em;
                    font-weight: bold;
                    margin-bottom: 0.5em;
                    margin-top: 0.5em;
                  }
                  .bienvenida-content h3 {
                    font-size: 1.25em;
                    font-weight: bold;
                    margin-bottom: 0.5em;
                    margin-top: 0.5em;
                  }
                  .bienvenida-content ul, .bienvenida-content ol {
                    margin-left: 1.5em;
                    margin-bottom: 1em;
                    line-height: 1.8;
                  }
                  .bienvenida-content ul {
                    list-style-type: disc;
                  }
                  .bienvenida-content ol {
                    list-style-type: decimal;
                  }
                  .bienvenida-content li {
                    margin-bottom: 0.3em;
                  }
                  .bienvenida-content a {
                    color: #2563eb;
                    text-decoration: underline;
                  }
                  .bienvenida-content .ql-align-center {
                    text-align: center;
                  }
                  .bienvenida-content .ql-align-right {
                    text-align: right;
                  }
                  .bienvenida-content .ql-align-justify {
                    text-align: justify;
                  }
                `}</style>
                
                {/* Imágenes de bienvenida - AHORA PRIMERO */}
                {bienvenida?.imagenes && bienvenida.imagenes.length > 0 && (
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    {bienvenida.imagenes.map((img, index) => (
                      <img 
                        key={index}
                        src={img} 
                        alt={`Imagen ${index + 1}`}
                        className="rounded-lg shadow-md w-full h-32 object-cover"
                      />
                    ))}
                  </div>
                )}
                
                <div 
                  className="bienvenida-content text-gray-700 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: bienvenida?.contenido || '<p>Cargando...</p>' }}
                />
              </CardContent>
            </Card>
          </div>

          {/* SECCIÓN ADICIONAL EDITABLE - Columna Derecha */}
          <div id="seccion-adicional">
            <Card className="shadow-2xl border-0 overflow-hidden h-full">
              <div className="bg-gradient-to-r from-green-500 to-teal-500 p-6 text-white">
                <h2 className="text-3xl font-bold flex items-center gap-3">
                  <i className="fas fa-info-circle"></i>
                  {seccionAdicional?.titulo || 'Nuestro Instituto'}
                </h2>
              </div>
              <CardContent className="p-6">
                <style>{`
                  .seccion-content p {
                    margin-bottom: 1em;
                    line-height: 1.6;
                  }
                  .seccion-content strong {
                    font-weight: 700;
                    color: #1f2937;
                  }
                  .seccion-content em {
                    font-style: italic;
                  }
                  .seccion-content u {
                    text-decoration: underline;
                  }
                  .seccion-content h1 {
                    font-size: 2em;
                    font-weight: bold;
                    margin-bottom: 0.5em;
                    margin-top: 0.5em;
                  }
                  .seccion-content h2 {
                    font-size: 1.5em;
                    font-weight: bold;
                    margin-bottom: 0.5em;
                    margin-top: 0.5em;
                  }
                  .seccion-content h3 {
                    font-size: 1.25em;
                    font-weight: bold;
                    margin-bottom: 0.5em;
                    margin-top: 0.5em;
                  }
                  .seccion-content ul, .seccion-content ol {
                    margin-left: 1.5em;
                    margin-bottom: 1em;
                    line-height: 1.8;
                  }
                  .seccion-content ul {
                    list-style-type: disc;
                  }
                  .seccion-content ol {
                    list-style-type: decimal;
                  }
                  .seccion-content li {
                    margin-bottom: 0.3em;
                  }
                  .seccion-content a {
                    color: #2563eb;
                    text-decoration: underline;
                  }
                  .seccion-content .ql-align-center {
                    text-align: center;
                  }
                  .seccion-content .ql-align-right {
                    text-align: right;
                  }
                  .seccion-content .ql-align-justify {
                    text-align: justify;
                  }
                `}</style>
                
                {/* Imágenes de la sección adicional - AHORA PRIMERO */}
                {seccionAdicional?.imagenes && seccionAdicional.imagenes.length > 0 && (
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    {seccionAdicional.imagenes.map((img, index) => (
                      <img 
                        key={index}
                        src={img} 
                        alt={`Imagen ${index + 1}`}
                        className="rounded-lg shadow-md w-full h-32 object-cover"
                      />
                    ))}
                  </div>
                )}
                
                <div 
                  className="seccion-content text-gray-700 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: seccionAdicional?.contenido || '<p>Cargando...</p>' }}
                />
              </CardContent>
            </Card>
          </div>
        </section>

        {/* BANNERS DE CLASES - Estilo Cards como Eventos */}
        {(bannerSemestreConfig.activo && clasesActivas.length > 0) || (bannerExtraConfig.activo && clasesExtracurriculares.length > 0) ? (
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              
              {/* CARD CLASES DEL SEMESTRE */}
              {bannerSemestreConfig.activo && clasesActivas.length > 0 && (
                <Card className="shadow-xl border-0 overflow-hidden hover:shadow-2xl transition-all duration-300">
                  {/* Header con color */}
                  <div 
                    className="p-4 text-white"
                    style={{ background: `linear-gradient(to right, ${bannerSemestreConfig.color_fondo || '#3b82f6'}, ${bannerSemestreConfig.color_fondo || '#3b82f6'}cc)` }}
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="font-bold text-xl flex items-center gap-2">
                        <i className="fas fa-graduation-cap"></i>
                        Clases Próximo Semestre
                      </h3>
                      <div className="flex gap-1.5">
                        {clasesActivas.map((_, idx) => (
                          <button
                            key={idx}
                            onClick={() => setClaseActualIndex(idx)}
                            className={`w-2.5 h-2.5 rounded-full transition-all ${idx === claseActualIndex ? 'bg-white' : 'bg-white/40'}`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Imagen */}
                  <div className="w-full h-48 overflow-hidden bg-gray-100">
                    {clasesActivas[claseActualIndex]?.imagen ? (
                      <img
                        src={clasesActivas[claseActualIndex].imagen}
                        alt={clasesActivas[claseActualIndex].titulo}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    ) : clasesActivas[claseActualIndex]?.profesor?.foto ? (
                      <img
                        src={clasesActivas[claseActualIndex].profesor.foto}
                        alt={clasesActivas[claseActualIndex].profesor.nombre}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-300">
                        <i className="fas fa-graduation-cap text-6xl"></i>
                      </div>
                    )}
                  </div>
                  
                  {/* Contenido */}
                  <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4" style={{ borderColor: bannerSemestreConfig.color_fondo || '#3b82f6' }}>
                    <h4 className="font-bold text-xl text-gray-800 mb-2">
                      {clasesActivas[claseActualIndex]?.titulo}
                    </h4>
                    {clasesActivas[claseActualIndex]?.descripcion && (
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                        {clasesActivas[claseActualIndex].descripcion}
                      </p>
                    )}
                    
                    {/* Profesor */}
                    {clasesActivas[claseActualIndex]?.profesor && (
                      <div className="flex items-center gap-3 bg-white p-3 rounded-lg">
                        <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                          {clasesActivas[claseActualIndex].profesor.foto ? (
                            <img
                              src={clasesActivas[claseActualIndex].profesor.foto}
                              alt={clasesActivas[claseActualIndex].profesor.nombre}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                              <i className="fas fa-user"></i>
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-800">{clasesActivas[claseActualIndex].profesor.nombre}</p>
                          <p className="text-xs text-gray-500">Profesor</p>
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              )}

              {/* CARD CLASES EXTRACURRICULARES Y MINISTERIALES */}
              {bannerExtraConfig.activo && clasesExtracurriculares.length > 0 && (
                <Card className="shadow-xl border-0 overflow-hidden hover:shadow-2xl transition-all duration-300">
                  {/* Header con color */}
                  <div 
                    className="p-4 text-white"
                    style={{ background: `linear-gradient(to right, ${bannerExtraConfig.color_fondo}, ${bannerExtraConfig.color_fondo}cc)` }}
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="font-bold text-xl flex items-center gap-2">
                        <i className="fas fa-book-open"></i>
                        Clases Extracurriculares y Ministeriales
                      </h3>
                      <div className="flex gap-1.5">
                        {clasesExtracurriculares.map((_, idx) => (
                          <button
                            key={idx}
                            onClick={() => setClaseExtraIndex(idx)}
                            className={`w-2.5 h-2.5 rounded-full transition-all ${idx === claseExtraIndex ? 'bg-white' : 'bg-white/40'}`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Imagen */}
                  <div className="w-full h-48 overflow-hidden bg-gray-100">
                    {clasesExtracurriculares[claseExtraIndex]?.imagen ? (
                      <img
                        src={clasesExtracurriculares[claseExtraIndex].imagen}
                        alt={clasesExtracurriculares[claseExtraIndex].titulo}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    ) : clasesExtracurriculares[claseExtraIndex]?.profesor?.foto ? (
                      <img
                        src={clasesExtracurriculares[claseExtraIndex].profesor.foto}
                        alt={clasesExtracurriculares[claseExtraIndex].profesor.nombre}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-300">
                        <i className="fas fa-church text-6xl"></i>
                      </div>
                    )}
                  </div>
                  
                  {/* Contenido */}
                  <div className="p-6 bg-gradient-to-r from-purple-50 to-fuchsia-50 border-l-4" style={{ borderColor: bannerExtraConfig.color_fondo }}>
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-bold text-xl text-gray-800">
                        {clasesExtracurriculares[claseExtraIndex]?.titulo}
                      </h4>
                      {clasesExtracurriculares[claseExtraIndex]?.es_ministerial && (
                        <span className="inline-flex items-center px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                          <i className="fas fa-church mr-1"></i>
                          Ministerial
                        </span>
                      )}
                    </div>
                    {clasesExtracurriculares[claseExtraIndex]?.descripcion && (
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                        {clasesExtracurriculares[claseExtraIndex].descripcion}
                      </p>
                    )}
                    
                    {/* Profesor */}
                    {clasesExtracurriculares[claseExtraIndex]?.profesor && (
                      <div className="flex items-center gap-3 bg-white p-3 rounded-lg">
                        <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                          {clasesExtracurriculares[claseExtraIndex].profesor.foto ? (
                            <img
                              src={clasesExtracurriculares[claseExtraIndex].profesor.foto}
                              alt={clasesExtracurriculares[claseExtraIndex].profesor.nombre}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                              <i className="fas fa-user"></i>
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-800">{clasesExtracurriculares[claseExtraIndex].profesor.nombre}</p>
                          <p className="text-xs text-gray-500">Profesor</p>
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              )}

            </div>
          </section>
        ) : null}

        {/* CARRUSEL DE PROFESORES/STAFF */}
        {profesores.length > 0 && (
          <section id="profesores" className="mb-16">
            <div className="text-center mb-8">
              <h2 className="text-4xl font-bold text-gray-800 mb-3 flex items-center justify-center gap-3">
                <i className="fas fa-chalkboard-teacher text-blue-600"></i>
                Nuestro Staff de Profesores y Colaboradores
              </h2>
              <p className="text-gray-600 text-lg">Conoce a nuestros experimentados docentes</p>
            </div>

            <div className="relative">
              {/* Grid de profesores */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {profesores.map((profesor) => (
                  <Card key={profesor.id} className="shadow-xl border-0 hover:shadow-2xl transition-all duration-300 overflow-hidden">
                    <CardContent className="p-6 text-center">
                      {/* Foto circular */}
                      <div className="w-32 h-32 mx-auto rounded-full overflow-hidden bg-gradient-to-br from-blue-400 to-indigo-500 p-1 mb-4">
                        <div className="w-full h-full rounded-full overflow-hidden bg-white">
                          {profesor.foto_url ? (
                            <img
                              src={profesor.foto_url.startsWith('http') ? profesor.foto_url : `${API}${profesor.foto_url}?t=${Date.now()}`}
                              alt={`${profesor.nombre} ${profesor.apellido}`}
                              className="w-full h-full object-cover"
                              loading="eager"
                              crossOrigin="anonymous"
                              onError={(e) => {
                                // Si es URL externa, no reintentar con API prefix
                                if (profesor.foto_url.startsWith('http')) {
                                  e.target.onerror = null;
                                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"%3E%3Crect fill="%23f3f4f6" width="100" height="100"/%3E%3Ctext x="50" y="50" text-anchor="middle" dy=".3em" fill="%239ca3af" font-size="40" font-family="Arial"%3E%F0%9F%91%A4%3C/text%3E%3C/svg%3E';
                                  return;
                                }
                                
                                // Para URLs internas, intentar retry
                                const retryCount = parseInt(e.target.dataset.retry || '0');
                                if (retryCount < 3) {
                                  e.target.dataset.retry = retryCount + 1;
                                  setTimeout(() => {
                                    e.target.src = `${API}${profesor.foto_url}?retry=${retryCount + 1}&t=${Date.now()}`;
                                  }, 500 * (retryCount + 1));
                                } else {
                                  e.target.onerror = null;
                                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"%3E%3Crect fill="%23f3f4f6" width="100" height="100"/%3E%3Ctext x="50" y="50" text-anchor="middle" dy=".3em" fill="%239ca3af" font-size="40" font-family="Arial"%3E%F0%9F%91%A4%3C/text%3E%3C/svg%3E';
                                }
                              }}
                            />
                          ) : (
                            <div className="flex items-center justify-center h-full">
                              <i className="fas fa-user text-5xl text-gray-400"></i>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Nombre */}
                      <h3 className="text-xl font-bold text-gray-800 mb-1">
                        {profesor.nombre} {profesor.apellido}
                      </h3>

                      {/* Ubicación */}
                      <p className="text-sm text-gray-600 mb-3 flex items-center justify-center gap-1">
                        <i className="fas fa-map-marker-alt text-blue-500"></i>
                        {profesor.ciudad}, {profesor.pais}
                      </p>

                      {/* Especialidades */}
                      {profesor.especialidades && profesor.especialidades.length > 0 && (
                        <div className="flex flex-wrap gap-1 justify-center mb-3">
                          {profesor.especialidades.slice(0, 3).map((esp, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs bg-blue-100 text-blue-700">
                              {esp}
                            </Badge>
                          ))}
                          {profesor.especialidades.length > 3 && (
                            <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">
                              +{profesor.especialidades.length - 3}
                            </Badge>
                          )}
                        </div>
                      )}

                      {/* Biografía resumida */}
                      <p className="text-sm text-gray-600 line-clamp-3">
                        {profesor.biografia}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* NOTICIAS PÚBLICAS */}
        <section id="noticias" className="mb-16">
          <div className="text-center mb-8">
            <h2 className="text-4xl font-bold text-gray-800 mb-3 flex items-center justify-center gap-3">
              <i className="fas fa-newspaper text-blue-600"></i>
              Noticias del IBA
            </h2>
            <p className="text-gray-600 text-lg">Mantente informado sobre nuestras actividades</p>
          </div>

          {noticiasPublicas.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <i className="fas fa-newspaper text-6xl mb-4"></i>
              <p className="text-xl">No hay noticias públicas por el momento</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {noticiasPublicas.map((noticia) => (
                <Card key={noticia.id} className="shadow-xl border-0 hover:shadow-2xl transition-all duration-300 overflow-hidden flex flex-col">
                  {/* Imagen de la noticia (si existe) */}
                  {noticia.imagenes && noticia.imagenes.length > 0 && (
                    <div className="w-full h-64 overflow-hidden">
                      <img 
                        src={noticia.imagenes[0].startsWith('http') ? noticia.imagenes[0] : `${API}${noticia.imagenes[0]}`}
                        alt={noticia.titulo}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  
                  <CardHeader className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white pb-4">
                    <CardTitle className="text-xl font-bold">{noticia.titulo}</CardTitle>
                    <div className="flex items-center gap-4 text-sm text-blue-100 mt-2">
                      <span><i className="fas fa-user mr-1"></i>{noticia.autor_nombre}</span>
                      <span><i className="fas fa-calendar mr-1"></i>{new Date(noticia.fecha_publicacion).toLocaleDateString('es-UY')}</span>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="pt-6 pb-6 flex-grow">
                    <p className="text-gray-700 leading-relaxed text-base whitespace-pre-line">
                      {noticia.contenido}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>

        {/* === Comité Evangelístico — Sección controlada por admin === */}
        {comiteSeccionActiva && (
          <section id="comite-en-vivo" className="mb-16">
            <div className={`p-6 rounded-t-xl text-white ${comiteTransmisiones.length > 0 ? 'bg-gradient-to-r from-rose-600 via-red-600 to-amber-600' : 'bg-gradient-to-r from-rose-500 to-amber-500'}`}>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  {comiteTransmisiones.length > 0 ? (
                    <div className="relative flex w-4 h-4">
                      <span className="absolute w-4 h-4 bg-white rounded-full animate-ping" />
                      <span className="relative w-4 h-4 bg-white rounded-full" />
                    </div>
                  ) : (
                    <i className="fas fa-hands-praying text-3xl"></i>
                  )}
                  <div>
                    <p className="text-xs uppercase tracking-wider opacity-90 font-semibold">
                      {comiteTransmisiones.length > 0 ? 'EN VIVO AHORA' : 'PRÓXIMAMENTE'}
                    </p>
                    <h2 className="text-3xl sm:text-4xl font-bold flex items-center gap-3">
                      <i className="fas fa-hands-praying"></i>
                      Comité Evangelístico
                    </h2>
                    <p className="text-rose-100 text-sm mt-1">
                      {comiteTransmisiones.length > 0
                        ? 'Acompañanos en vivo en esta actividad evangelística'
                        : 'Pronto vamos a transmitir en vivo — quedate atento'}
                    </p>
                  </div>
                </div>
                {comiteTransmisiones.length > 0 && (
                  <span className="bg-white/25 text-white text-xs font-semibold px-3 py-1.5 rounded-full backdrop-blur-sm">
                    🔴 {comiteTransmisiones.length} transmisión{comiteTransmisiones.length === 1 ? '' : 'es'} activa{comiteTransmisiones.length === 1 ? '' : 's'}
                  </span>
                )}
              </div>
            </div>
            <div className="bg-white rounded-b-xl shadow-xl border border-rose-200 p-4 sm:p-6">
              {comiteTransmisiones.length > 0 && comiteYoutubeEmbed ? (
                <>
                  {/* === MODO LIVE === */}
                  <div className="space-y-2 mb-4">
                    {comiteTransmisiones.map((t) => (
                      <div key={t.id} className="bg-rose-50 border border-rose-200 rounded-lg p-3 flex items-center gap-3" data-testid={`landing-comite-transmision-${t.id}`}>
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rose-500 to-amber-500 flex items-center justify-center text-white shrink-0">
                          <i className="fas fa-hands-praying"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-rose-900 truncate">{t.titulo}</p>
                          <p className="text-xs text-gray-600 truncate">
                            {t.ubicacion && <span><i className="fas fa-map-marker-alt mr-1"></i>{t.ubicacion} · </span>}
                            Transmite: <strong>{t.transmision_iniciada_por_nombre || t.transmisor_nombre || 'Comité IBA Uruguay'}</strong>
                          </p>
                        </div>
                        <span className="hidden sm:inline-flex items-center gap-1 bg-red-600 text-white text-[10px] font-bold uppercase px-2 py-0.5 rounded-full">
                          <span className="relative flex w-2 h-2">
                            <span className="absolute w-2 h-2 bg-white rounded-full animate-ping" />
                            <span className="relative w-2 h-2 bg-white rounded-full" />
                          </span>
                          EN VIVO
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="aspect-video w-full bg-black rounded-xl overflow-hidden shadow-xl" data-testid="landing-comite-youtube-embed">
                    <iframe
                      title="Transmisión en vivo del Comité Evangelístico IBA Uruguay"
                      src={comiteYoutubeEmbed}
                      allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full border-0"
                    />
                  </div>
                </>
              ) : (
                /* === MODO PREVIEW (sin live aún) === */
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-center" data-testid="landing-comite-preview">
                  <div>
                    <div className="bg-gradient-to-br from-rose-50 via-amber-50 to-white border-2 border-rose-200 rounded-xl p-5 shadow-md">
                      <div className="flex items-center gap-2 mb-3">
                        <i className="fas fa-calendar-check text-rose-600 text-xl"></i>
                        <p className="text-xs font-bold text-rose-700 uppercase tracking-wider">Próxima transmisión pública</p>
                      </div>
                      {comiteProxima ? (
                        <>
                          <h3 className="text-2xl font-bold text-rose-900 mb-2">{comiteProxima.titulo}</h3>
                          {comiteProxima.descripcion && (
                            <p className="text-sm text-gray-700 mb-3 line-clamp-3">{comiteProxima.descripcion}</p>
                          )}
                          <div className="space-y-1.5 text-sm">
                            <div className="flex items-center text-rose-900">
                              <i className="fas fa-calendar-day w-5 text-rose-600"></i>
                              <span className="font-semibold">
                                {new Date(comiteProxima.fecha_inicio).toLocaleDateString('es-UY', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
                              </span>
                            </div>
                            <div className="flex items-center text-gray-700">
                              <i className="fas fa-clock w-5 text-rose-600"></i>
                              <span>
                                {new Date(comiteProxima.fecha_inicio).toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit' })} hrs
                              </span>
                            </div>
                            {comiteProxima.ubicacion && (
                              <div className="flex items-center text-gray-700">
                                <i className="fas fa-map-marker-alt w-5 text-rose-600"></i>
                                <span>{comiteProxima.ubicacion}</span>
                              </div>
                            )}
                            {comiteProxima.transmisor_nombre && (
                              <div className="flex items-center text-gray-700 text-xs">
                                <i className="fas fa-tower-broadcast w-5 text-rose-600"></i>
                                <span>Transmite: <strong>{comiteProxima.transmisor_nombre}</strong></span>
                              </div>
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          <h3 className="text-xl font-bold text-rose-900 mb-2">¡Pronto en vivo!</h3>
                          <p className="text-sm text-gray-700">
                            En breve transmitiremos una nueva actividad evangelística. Suscribite al canal de YouTube para no perderte ninguna.
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-gray-900 via-rose-900 to-amber-900 rounded-xl aspect-video flex flex-col items-center justify-center text-white p-4 shadow-xl">
                    <i className="fas fa-hands-praying text-7xl opacity-30 mb-3"></i>
                    <p className="text-2xl font-bold mb-1">Comité Evangelístico</p>
                    <p className="text-sm opacity-80 text-center mb-4">IBA Uruguay · Evangelismo en acción</p>
                    {comiteYoutubeChannel && (
                      <a
                        href={comiteYoutubeChannel}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-red-600 hover:bg-red-700 text-white text-sm font-semibold px-4 py-2 rounded-lg flex items-center gap-2"
                      >
                        <i className="fab fa-youtube text-base"></i> Suscribirse al canal
                      </a>
                    )}
                  </div>
                </div>
              )}

              <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-rose-50 border border-rose-200 rounded-lg p-3">
                <p className="text-sm text-rose-900">
                  📢 <strong>Acompañanos en las actividades evangelísticas del Comité IBA Uruguay.</strong> Si querés ser parte del Instituto Bíblico,{' '}
                  <button onClick={() => navigate('/login')} className="text-rose-700 underline font-semibold hover:text-rose-900">ingresá acá</button>.
                </p>
                {comiteYoutubeChannel && comiteTransmisiones.length > 0 && (
                  <a
                    href={comiteYoutubeChannel}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-3 py-2 rounded-lg flex items-center gap-2 shrink-0"
                  >
                    <i className="fab fa-youtube"></i> Ver en YouTube
                  </a>
                )}
              </div>
            </div>
          </section>
        )}

        {/* SECCIÓN DE EVENTOS - Debajo de Noticias en 2 columnas */}
        <section id="eventos" className="mb-16">
          <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-6 rounded-t-xl text-white text-center">
            <h2 className="text-4xl font-bold flex items-center justify-center gap-3">
              <i className="fas fa-calendar-alt"></i>
              Próximos Eventos
            </h2>
            <p className="text-purple-100 mt-2">Únete a nuestras actividades y eventos especiales</p>
          </div>
          
          {eventosPublicos.length === 0 ? (
            <div className="bg-white rounded-b-xl shadow-xl p-12 text-center text-gray-500">
              <i className="fas fa-calendar-times text-6xl mb-4 text-purple-300"></i>
              <p className="text-xl">No hay eventos públicos próximos</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
              {eventosPublicos.map((evento) => {
                const isToday = isEventToday(evento.fecha_inicio);
                
                return (
                  <Card key={evento.id} className="shadow-xl border-0 overflow-hidden hover:shadow-2xl transition-all duration-300">
                    {/* Imagen del evento (prioridad: imagen_url de Imgur, luego imagenes antiguas) */}
                    {(evento.imagen_url || (evento.imagenes && evento.imagenes.length > 0)) && (
                      <div className="w-full h-48 overflow-hidden">
                        <img 
                          src={evento.imagen_url || (evento.imagenes[0].startsWith('http') ? evento.imagenes[0] : `${API}${evento.imagenes[0]}`)}
                          alt={evento.titulo}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                    
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 border-l-4 border-purple-500">
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="font-bold text-2xl text-gray-800 flex-1">{evento.titulo}</h3>
                        <Badge className="bg-purple-600 text-white font-bold flex-shrink-0 ml-2 px-3 py-1">
                          <i className={`fas ${evento.tipo === 'online' ? 'fa-video' : 'fa-map-marker-alt'} mr-2`}></i>
                          {evento.tipo === 'online' ? 'Online' : 'Presencial'}
                        </Badge>
                      </div>
                      <p className="text-gray-700 text-base mb-4 leading-relaxed">{evento.descripcion}</p>
                      
                      <div className="space-y-3 text-sm text-gray-700">
                        <div className="flex items-center gap-3 bg-white p-2 rounded-lg">
                          <i className="fas fa-calendar text-purple-600 text-lg"></i>
                          <span className="font-semibold">{formatDate(evento.fecha_inicio)}</span>
                        </div>
                        
                        <div className="flex items-center gap-3 bg-white p-2 rounded-lg">
                          <i className="fas fa-clock text-purple-600 text-lg"></i>
                          <span className="font-semibold">{formatTime(evento.fecha_inicio)} - {formatTime(evento.fecha_fin)}</span>
                        </div>
                        
                        {evento.tipo === 'presencial' && evento.ubicacion && (
                          <div className="flex items-center gap-3 bg-white p-2 rounded-lg">
                            <i className="fas fa-map-marker-alt text-purple-600 text-lg"></i>
                            <span>{evento.ubicacion}</span>
                          </div>
                        )}
                        
                        {/* VENTANA DE YOUTUBE - Si el evento tiene youtube_url */}
                        {evento.youtube_url && getYouTubeId(evento.youtube_url) && (
                          <div className="mt-4">
                            <div className="bg-gray-900 rounded-xl overflow-hidden shadow-xl">
                              <div className="flex items-center justify-between px-4 py-2 bg-red-600">
                                <div className="flex items-center gap-2 text-white">
                                  <i className="fab fa-youtube"></i>
                                  <span className="font-semibold text-sm">
                                    {isToday ? '🔴 EN VIVO' : '📅 Transmisión Programada'}
                                  </span>
                                </div>
                                <a
                                  href={evento.youtube_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-white text-xs hover:underline"
                                >
                                  Ver en YouTube <i className="fas fa-external-link-alt ml-1"></i>
                                </a>
                              </div>
                              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                                <iframe
                                  className="absolute top-0 left-0 w-full h-full"
                                  src={`https://www.youtube.com/embed/${getYouTubeId(evento.youtube_url)}?rel=0`}
                                  title={evento.titulo}
                                  frameBorder="0"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                ></iframe>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* BOTÓN DE ZOOM - Solo si NO tiene YouTube y es online */}
                        {evento.tipo === 'online' && evento.link_zoom && !evento.youtube_url && (
                          <div className="mt-4">
                            {isToday ? (
                              <a
                                href={evento.link_zoom}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold w-full justify-center shadow-lg"
                              >
                                <i className="fas fa-video text-lg"></i>
                                Unirse a Zoom Ahora
                              </a>
                            ) : (
                              <button
                                disabled
                                className="inline-flex items-center gap-2 px-6 py-3 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed font-semibold w-full justify-center"
                                title="El enlace estará disponible el día del evento"
                              >
                                <i className="fas fa-video text-lg"></i>
                                Zoom disponible el día del evento
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </section>

        {/* SECCIÓN FUTURA - Placeholder para contenido adicional */}
        {/* 
        <section id="futuro" className="mb-16">
          <Card className="shadow-2xl border-0">
            <CardHeader className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white pb-6">
              <CardTitle className="text-2xl">Sección Futura</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <p className="text-gray-600">Espacio reservado para contenido futuro...</p>
            </CardContent>
          </Card>
        </section>
        */}

        {/* SECCIONES DINÁMICAS */}
        {seccionesDinamicas.length > 0 && (
          <section className="mb-16 max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {seccionesDinamicas.map((seccion) => (
                <Card key={seccion.id} className="shadow-2xl border-0 overflow-hidden flex flex-col">
                  <div className="bg-gradient-to-r from-indigo-500 to-purple-500 p-6 text-white">
                    <h2 className="text-2xl font-bold flex items-center gap-3">
                      <i className="fas fa-star"></i>
                      {seccion.titulo}
                    </h2>
                  </div>
                  <CardContent className="p-6 flex-grow">
                    <style>{`
                      .seccion-dinamica-content p {
                        margin-bottom: 1em;
                        line-height: 1.6;
                      }
                      .seccion-dinamica-content strong {
                        font-weight: 700;
                        color: #1f2937;
                      }
                      .seccion-dinamica-content em {
                        font-style: italic;
                      }
                      .seccion-dinamica-content h1 {
                        font-size: 2em;
                        font-weight: bold;
                        margin-bottom: 0.5em;
                      }
                      .seccion-dinamica-content h2 {
                        font-size: 1.5em;
                        font-weight: bold;
                        margin-bottom: 0.5em;
                      }
                      .seccion-dinamica-content ul, .seccion-dinamica-content ol {
                        margin-left: 1.5em;
                        margin-bottom: 1em;
                        line-height: 1.8;
                      }
                      .seccion-dinamica-content ul {
                        list-style-type: disc;
                      }
                    `}</style>
                    
                    {seccion.tipo === 'texto_imagen' && seccion.imagen_url ? (
                      <div className="space-y-4">
                        <img 
                          src={seccion.imagen_url.startsWith('http') ? seccion.imagen_url : `${API}${seccion.imagen_url}`}
                          alt={seccion.titulo}
                          className="rounded-lg shadow-lg w-full h-48 object-cover"
                          loading="eager"
                          onError={(e) => {
                            // Si es URL externa, no hacer retry
                            if (seccion.imagen_url.startsWith('http')) {
                              e.target.style.display = 'none';
                              return;
                            }
                            // Para URLs internas, intentar retry
                            const retryCount = parseInt(e.target.dataset.retry || '0');
                            if (retryCount < 3) {
                              e.target.dataset.retry = retryCount + 1;
                              setTimeout(() => {
                                e.target.src = `${API}${seccion.imagen_url}?retry=${retryCount + 1}&t=${Date.now()}`;
                              }, 500 * (retryCount + 1));
                            }
                          }}
                        />
                        <div 
                          className="seccion-dinamica-content text-gray-700 leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: seccion.contenido }}
                        />
                      </div>
                    ) : seccion.tipo === 'imagen' && seccion.imagen_url ? (
                      <img 
                        src={seccion.imagen_url.startsWith('http') ? seccion.imagen_url : `${API}${seccion.imagen_url}`}
                        alt={seccion.titulo}
                        className="rounded-lg shadow-lg w-full h-64 object-cover"
                        loading="eager"
                        onError={(e) => {
                          // Si es URL externa, no hacer retry
                          if (seccion.imagen_url.startsWith('http')) {
                            e.target.style.display = 'none';
                            return;
                          }
                          // Para URLs internas, intentar retry
                          const retryCount = parseInt(e.target.dataset.retry || '0');
                          if (retryCount < 3) {
                            e.target.dataset.retry = retryCount + 1;
                            setTimeout(() => {
                              e.target.src = `${API}${seccion.imagen_url}?retry=${retryCount + 1}&t=${Date.now()}`;
                            }, 500 * (retryCount + 1));
                          }
                        }}
                      />
                    ) : seccion.tipo === 'ubicacion' && seccion.mapa_iframe ? (
                      <div className="space-y-4">
                        <div 
                          className="seccion-dinamica-content text-gray-700 leading-relaxed mb-4"
                          dangerouslySetInnerHTML={{ __html: seccion.contenido }}
                        />
                        <div 
                          className="w-full rounded-lg overflow-hidden shadow-lg border-2 border-gray-200"
                          style={{ height: '350px' }}
                          dangerouslySetInnerHTML={{ __html: seccion.mapa_iframe }}
                        />
                      </div>
                    ) : (
                      <div 
                        className="seccion-dinamica-content text-gray-700 leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: seccion.contenido }}
                      />
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* RED DE ORACIÓN */}
        <section id="oracion" className="mb-16">
          <div className="text-center mb-8">
            <h2 className="text-4xl font-bold text-gray-800 mb-3 flex items-center justify-center gap-3">
              <i className="fas fa-praying-hands text-amber-600"></i>
              Red de Oración IBA Uruguay
            </h2>
            <p className="text-gray-600 text-lg">Comparte tu petición y oremos juntos</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Formulario de pedido */}
            <Card className="shadow-2xl border-0">
              <CardHeader className="bg-gradient-to-r from-amber-500 to-orange-500 text-white pb-6">
                <CardTitle className="text-2xl flex items-center gap-2">
                  <i className="fas fa-hands-praying"></i>
                  Comparte tu Pedido
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {pedidoEnviado ? (
                  <div className="bg-green-50 border-2 border-green-500 text-green-800 px-6 py-8 rounded-lg text-center">
                    <i className="fas fa-check-circle text-5xl mb-4 text-green-600"></i>
                    <h3 className="text-2xl font-bold mb-2">¡Pedido Recibido!</h3>
                    <p className="text-lg">Estaremos orando por ti. Que Dios te bendiga.</p>
                  </div>
                ) : (
                  <form onSubmit={handleEnviarPedido} className="space-y-4">
                    <div>
                      <Label htmlFor="nombre">Tu Nombre *</Label>
                      <Input
                        id="nombre"
                        value={pedidoOracion.nombre}
                        onChange={(e) => setPedidoOracion({ ...pedidoOracion, nombre: e.target.value })}
                        placeholder="¿Cómo te llamas?"
                        required
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email-oracion">Email (opcional)</Label>
                      <Input
                        id="email-oracion"
                        type="email"
                        value={pedidoOracion.email}
                        onChange={(e) => setPedidoOracion({ ...pedidoOracion, email: e.target.value })}
                        placeholder="tu@email.com"
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="pedido">Tu Pedido de Oración *</Label>
                      <Textarea
                        id="pedido"
                        value={pedidoOracion.pedido}
                        onChange={(e) => setPedidoOracion({ ...pedidoOracion, pedido: e.target.value })}
                        placeholder="Comparte tu petición..."
                        rows={5}
                        required
                        className="mt-1"
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="publico"
                        checked={pedidoOracion.publico}
                        onChange={(e) => setPedidoOracion({ ...pedidoOracion, publico: e.target.checked })}
                        className="w-4 h-4 text-amber-600"
                      />
                      <Label htmlFor="publico" className="text-sm text-gray-700 cursor-pointer">
                        Permitir que mi pedido aparezca públicamente (solo se mostrarán iniciales)
                      </Label>
                    </div>

                    <Button 
                      type="submit" 
                      disabled={enviandoPedido}
                      className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold py-3 text-lg shadow-lg"
                    >
                      {enviandoPedido ? (
                        <>
                          <i className="fas fa-spinner fa-spin mr-2"></i>
                          Enviando...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-paper-plane mr-2"></i>
                          Enviar Pedido
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>

            {/* Pedidos recientes públicos */}
            <Card className="shadow-2xl border-0">
              <CardHeader className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white pb-6">
                <CardTitle className="text-2xl flex items-center gap-2">
                  <i className="fas fa-heart"></i>
                  Pedidos Recientes
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {pedidosPublicos.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <i className="fas fa-praying-hands text-4xl mb-3"></i>
                    <p>No hay pedidos públicos recientes</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pedidosPublicos.map((pedido, index) => (
                      <div 
                        key={pedido.id || index} 
                        className="bg-purple-50 border-l-4 border-purple-500 p-4 rounded-r-lg"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <i className="fas fa-user-circle text-purple-600"></i>
                          <span className="font-medium text-purple-900">{pedido.nombre}</span>
                          <span className="text-xs text-gray-500">
                            {new Date(pedido.fecha_creacion).toLocaleDateString('es-UY')}
                          </span>
                        </div>
                        <p className="text-gray-700 text-sm italic">"{pedido.pedido}"</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </section>
      </main>

      {/* FORMULARIO DE CONSULTA */}
      <section id="consultas" className="py-16 px-4 bg-gradient-to-b from-white to-gray-100">
        <div className="container mx-auto max-w-7xl">
          <FormularioConsultaVisitante />
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-gradient-to-r from-gray-900 to-gray-800 text-white py-8 mt-16">
        <div className="container mx-auto px-4 text-center">
          <div className="mb-4">
            <h3 className="text-2xl font-bold mb-2">Instituto Bíblico Apostólico</h3>
            <p className="text-gray-300">Formando líderes para el Reino de Dios</p>
          </div>
          
          <div className="flex justify-center gap-6 mb-4 flex-wrap">
            {redesSociales.facebook_url && (
              <a 
                href={redesSociales.facebook_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-blue-500 transition transform hover:scale-110"
                title="Facebook"
              >
                <i className="fab fa-facebook text-3xl"></i>
              </a>
            )}
            {redesSociales.instagram_url && (
              <a 
                href={redesSociales.instagram_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-pink-500 transition transform hover:scale-110"
                title="Instagram"
              >
                <i className="fab fa-instagram text-3xl"></i>
              </a>
            )}
            {redesSociales.youtube_url && (
              <a 
                href={redesSociales.youtube_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-red-500 transition transform hover:scale-110"
                title="YouTube"
              >
                <i className="fab fa-youtube text-3xl"></i>
              </a>
            )}
            {redesSociales.twitter_url && (
              <a 
                href={redesSociales.twitter_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-sky-400 transition transform hover:scale-110"
                title="Twitter / X"
              >
                <i className="fab fa-twitter text-3xl"></i>
              </a>
            )}
            {redesSociales.linkedin_url && (
              <a 
                href={redesSociales.linkedin_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-blue-600 transition transform hover:scale-110"
                title="LinkedIn"
              >
                <i className="fab fa-linkedin text-3xl"></i>
              </a>
            )}
            {redesSociales.tiktok_url && (
              <a 
                href={redesSociales.tiktok_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-white transition transform hover:scale-110"
                title="TikTok"
              >
                <i className="fab fa-tiktok text-3xl"></i>
              </a>
            )}
            {redesSociales.webpage_url_1 && (
              <a 
                href={redesSociales.webpage_url_1} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-purple-400 transition transform hover:scale-110"
                title={redesSociales.webpage_url_1_nombre || 'Sitio Web'}
              >
                <i className="fas fa-globe text-3xl"></i>
              </a>
            )}
            {redesSociales.webpage_url_2 && (
              <a 
                href={redesSociales.webpage_url_2} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-purple-400 transition transform hover:scale-110"
                title={redesSociales.webpage_url_2_nombre || 'Sitio Web'}
              >
                <i className="fas fa-external-link-alt text-3xl"></i>
              </a>
            )}
            {redesSociales.webpage_url_3 && (
              <a 
                href={redesSociales.webpage_url_3} 
                target="_blank" 
                rel="noopener noreferrer"
                className="transition transform hover:scale-110 hover:opacity-80"
                title={redesSociales.webpage_url_3_nombre || 'Sitio Web'}
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_iba-domain-fix/artifacts/06woeifv_logo_square.png" 
                  alt="Emergent" 
                  className="h-10 w-10 rounded-full"
                />
              </a>
            )}
          </div>
          
          <p className="text-sm text-gray-400">
            © 2025 Instituto Bíblico Apostólico - Uruguay. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
